import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ApiService from "../services/api.service";

const SignupSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").required("nombre requerido"),
  district: Yup.string().nullable(),
  number: Yup.string().required("número requerido"),
  inside: Yup.string().required("interior requerido"),
  link: Yup.string().url().required("link google maps requerido"),
  type_address: Yup.string().nullable(),
  leave_your_order: Yup.string().nullable(),
  latitude: Yup.number().nullable(),
  longitude: Yup.number().nullable(),
});

/**
 * Extrae coordenadas de una URL de Google Maps
 */
const extractCoordinates = (url) => {
  if (!url) return { lat: null, lng: null };

  try {
    // Formato 1: ?q=lat,lng
    const queryMatch = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (queryMatch) {
      return {
        lat: parseFloat(queryMatch[1]),
        lng: parseFloat(queryMatch[2]),
      };
    }

    // Formato 2: @lat,lng
    const atMatch = url.match(/[@\/](-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (atMatch) {
      return {
        lat: parseFloat(atMatch[1]),
        lng: parseFloat(atMatch[2]),
      };
    }

    // Formato 3: !3dlat!4dlng (formato interno de Google)
    const internalMatch = url.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
    if (internalMatch) {
      return {
        lat: parseFloat(internalMatch[1]),
        lng: parseFloat(internalMatch[2]),
      };
    }

    return { lat: null, lng: null };
  } catch (error) {
    return { lat: null, lng: null };
  }
};

const VerAddressForm = ({ tokenJwt, onHandlerSubmit, address }) => {
  const [districts, setDistricts] = useState([]);
  const [district, setDistrict] = useState(null);
  const [typeAddress, setTypeAddress] = useState(null);
  const [typeLeaveYourOrder, setTypeLeaveYourOrder] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [coordinatesExtracted, setCoordinatesExtracted] = useState(false);

  const [formikRef, setFormikRef] = useState(null);
  const [beforeHour, setBeforeHour] = useState(false);

  // Google Places search
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowDropdown(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 3) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      const results = await ApiService.placesAutocomplete(tokenJwt, value);
      setSuggestions(results);
      setShowDropdown(results.length > 0);
      setSearchLoading(false);
    }, 500);
  };

  const handleSelectSuggestion = async (suggestion, setFieldValue) => {
    setShowDropdown(false);
    setSearchQuery(suggestion.description);
    setSuggestions([]);
    setSearchLoading(true);
    const details = await ApiService.placeDetails(tokenJwt, suggestion.place_id);
    if (details) {
      const coords = { lat: details.latitude, lng: details.longitude };
      setCoordinates(coords);
      setCoordinatesExtracted(true);
      setFieldValue('link', details.url_google);
      setFieldValue('latitude', details.latitude);
      setFieldValue('longitude', details.longitude);
    }
    setSearchLoading(false);
  };

  //const [address, setAddress] = useState(_address);
  useEffect(() => {
    if (address) {
      setDistrict({ value: address.ubigeo, label: address.district });
      setTypeAddress({
        value: address.type_address,
        label: address.type_address,
      });
      setTypeLeaveYourOrder({
        value: address.leave_your_order,
        label: address.leave_your_order,
      });
      setBeforeHour(address.before_hour || false);

      // Cargar coordenadas existentes o extraer del URL
      if (address.latitude && address.longitude) {
        const coords = { lat: address.latitude, lng: address.longitude };
        setCoordinates(coords);
        setCoordinatesExtracted(true);
        // Actualizar Formik si ya está inicializado
        if (formikRef) {
          formikRef.setFieldValue("latitude", coords.lat);
          formikRef.setFieldValue("longitude", coords.lng);
        }
      } else if (address.url_google) {
        const coords = extractCoordinates(address.url_google);
        setCoordinates(coords);
        setCoordinatesExtracted(coords.lat !== null && coords.lng !== null);
        // Actualizar Formik si ya está inicializado
        if (formikRef && coords.lat !== null && coords.lng !== null) {
          formikRef.setFieldValue("latitude", coords.lat);
          formikRef.setFieldValue("longitude", coords.lng);
        }
      }
    }
  }, [address, formikRef]);

  useEffect(() => {
    (async () => {
      if (tokenJwt) {
        const results = await ApiService.getDisttricts(tokenJwt);

        const options = results.map((result) => ({
          value: result.id,
          label: result.name,
        }));

        setDistricts(options);
      }
    })();
  }, [tokenJwt]);

  return (
    <Formik
      initialValues={{
        type_address: address ? address.type_address : "",
        leave_your_order: address ? address.leave_your_order : "",
        name: address ? address.address : "",
        district: address ? address.ubigeo : "",
        number: address ? address.number : "",
        inside: address ? address.interior : "",
        link: address ? address.url_google : "",
        latitude: address ? address.latitude : null,
        longitude: address ? address.longitude : null,
      }}
      enableReinitialize
      validationSchema={SignupSchema}
      onSubmit={(values, formikBags) => {
        onHandlerSubmit(
          {
            ...values,
            ubigeo: district.value,
            district: district.label,
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            before_hour:
              values.leave_your_order === "Conserje" ? beforeHour : false,
          },
          formikBags,
        );
      }}
    >
      {(formikProps) => {
        const {
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
        } = formikProps;

        // Guardar referencia de Formik
        if (!formikRef) {
          setFormikRef(formikProps);
        }

        return (
          <Form autoComplete='off'>
            <div>
              <div className='form-group row'>
                <label className='col-xl-3 col-md-4'>
                  <span>*</span> AV / CALLE / JIRÓN
                </label>
                <div style={{ width: "50%" }}>
                  <Select
                    name='type_address'
                    options={[
                      { value: "AV", label: "AVENIDA" },
                      { value: "CALLE", label: "CALLE" },
                      { value: "JIRON", label: "JIRON" },
                    ]}
                    value={typeAddress}
                    onChange={(option) => {
                      setTypeAddress(option);
                      setFieldValue("type_address", option.value);
                    }}
                  />
                  {errors.type_address && touched.type_address ? (
                    <div className='alert alert-danger' role='alert'>
                      {errors.type_address}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className='form-group row'>
                <label className='col-xl-3 col-md-4'>
                  <span>*</span> Nombre
                </label>
                <div style={{ width: "50%" }}>
                  <input
                    name='name'
                    type='text'
                    className='form-control'
                    value={values.name}
                    onChange={handleChange}
                  />
                  {errors.name && touched.name ? (
                    <div className='alert alert-danger' role='alert'>
                      {errors.name}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className='form-group row'>
                <label className='col-xl-3 col-md-4'>
                  <span>*</span> Número
                </label>
                <div style={{ width: "50%" }}>
                  <input
                    name='number'
                    type='text'
                    className='form-control'
                    value={values.number}
                    onChange={handleChange}
                  />
                  {errors.number && touched.number ? (
                    <div className='alert alert-danger' role='alert'>
                      {errors.number}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className='form-group row'>
                <label className='col-xl-3 col-md-4'>
                  <span>*</span> Interior
                </label>
                <div style={{ width: "50%" }}>
                  <input
                    name='inside'
                    type='text'
                    className='form-control'
                    value={values.inside}
                    onChange={handleChange}
                  />
                  {errors.inside && touched.inside ? (
                    <div className='alert alert-danger' role='alert'>
                      {errors.inside}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className='form-group row'>
                <label className='col-xl-3 col-md-4'>
                  <span>*</span> Distrito
                </label>
                <div style={{ width: "50%" }}>
                  <Select
                    name='district'
                    options={districts}
                    value={district}
                    onChange={(option) => {
                      setDistrict(option);
                      setFieldValue("district", option.value);
                    }}
                  />
                  {errors.district && touched.district ? (
                    <div className='alert alert-danger' role='alert'>
                      {errors.district}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className='form-group row'>
                <label className='col-xl-3 col-md-4'>Buscar en Google</label>
                <div style={{ width: "50%", position: "relative" }}>
                  <div style={{ position: "relative" }}>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Ej: Ca. Los Pinos 156, Miraflores'
                      value={searchQuery}
                      onChange={handleSearchChange}
                      autoComplete='off'
                    />
                    {searchLoading && (
                      <span style={{ position: "absolute", right: 10, top: 8 }}>⏳</span>
                    )}
                  </div>
                  {showDropdown && suggestions.length > 0 && (
                    <ul style={{ listStyle: "none", margin: 0, padding: 0, border: "1px solid #ccc", borderRadius: 4, background: "#fff", position: "absolute", zIndex: 999, width: "100%", maxHeight: 200, overflowY: "auto" }}>
                      {suggestions.map((s) => (
                        <li
                          key={s.place_id}
                          style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
                          onMouseDown={() => handleSelectSuggestion(s, setFieldValue)}
                        >
                          📍 {s.description}
                        </li>
                      ))}
                    </ul>
                  )}
                  <small className='form-text text-muted'>
                    Escribe la dirección y selecciona el resultado.
                  </small>
                </div>
              </div>

              <div className='form-group row'>
                <label className='col-xl-3 col-md-4'>
                  <span>*</span> Link en google maps
                </label>
                <div style={{ width: "50%" }}>
                  <input
                    name='link'
                    type='text'
                    className='form-control'
                    value={values.link}
                    onChange={(e) => {
                      const urlValue = e.target.value;
                      setFieldValue("link", urlValue);
                      const coords = extractCoordinates(urlValue);
                      if (coords.lat !== null && coords.lng !== null) {
                        setCoordinates(coords);
                        setCoordinatesExtracted(true);
                        setFieldValue("latitude", coords.lat);
                        setFieldValue("longitude", coords.lng);
                      } else {
                        setCoordinatesExtracted(false);
                      }
                    }}
                  />
                  <a
                    href='https://www.google.com/maps/@-12.0665327,-77.031246,15.74z'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <small id='linkHelp' className='form-text'>
                      Ir a google maps
                    </small>
                  </a>
                  {errors.link && touched.link ? (
                    <div className='alert alert-danger' role='alert'>
                      {errors.link}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className='form-group row'>
                <label className='col-xl-3 col-md-4'>Coordenadas GPS</label>
                <div style={{ width: "50%" }}>
                  <div className='row'>
                    <div className='col-md-6'>
                      <label style={{ fontSize: "0.85rem", color: "#666" }}>
                        Latitud
                      </label>
                      <input
                        name='latitude'
                        type='number'
                        step='any'
                        className='form-control'
                        value={coordinates.lat || ""}
                        onChange={(e) => {
                          const lat = e.target.value
                            ? parseFloat(e.target.value)
                            : null;
                          setCoordinates({ ...coordinates, lat });
                          setFieldValue("latitude", lat);
                          setCoordinatesExtracted(true);
                        }}
                        placeholder='Ej: -12.0930'
                      />
                    </div>
                    <div className='col-md-6'>
                      <label style={{ fontSize: "0.85rem", color: "#666" }}>
                        Longitud
                      </label>
                      <input
                        name='longitude'
                        type='number'
                        step='any'
                        className='form-control'
                        value={coordinates.lng || ""}
                        onChange={(e) => {
                          const lng = e.target.value
                            ? parseFloat(e.target.value)
                            : null;
                          setCoordinates({ ...coordinates, lng });
                          setFieldValue("longitude", lng);
                          setCoordinatesExtracted(true);
                        }}
                        placeholder='Ej: -76.9959'
                      />
                    </div>
                  </div>
                  {coordinatesExtracted &&
                  coordinates.lat &&
                  coordinates.lng ? (
                    <small className='form-text text-success'>
                      ✓ Coordenadas detectadas automáticamente
                    </small>
                  ) : (
                    <small className='form-text text-warning'>
                      ⚠ No se pudieron extraer coordenadas del link. Ingrésalas
                      manualmente.
                    </small>
                  )}
                  {coordinates.lat && coordinates.lng && (
                    <a
                      href={`https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        fontSize: "0.85rem",
                        marginTop: "5px",
                        display: "block",
                      }}
                    >
                      Ver ubicación en mapa
                    </a>
                  )}
                </div>
              </div>

              <div className='form-group row'>
                <label className='col-xl-3 col-md-4'>
                  <span>*</span> ¿Con quién dejamos tu pedido?
                </label>
                <div style={{ width: "50%" }}>
                  <Select
                    name='leave_your_order'
                    options={[
                      { value: "Conserje", label: "Conserje" },
                      { value: "Cliente recoge", label: "Cliente recoge" },
                    ]}
                    value={typeLeaveYourOrder}
                    onChange={(option) => {
                      setTypeLeaveYourOrder(option);
                      setFieldValue("leave_your_order", option.value);
                    }}
                  />
                  {errors.leave_your_order && touched.leave_your_order ? (
                    <div className='alert alert-danger' role='alert'>
                      {errors.leave_your_order}
                    </div>
                  ) : null}
                </div>
              </div>

              {values.leave_your_order === "Conserje" && (
                <div className='form-group row'>
                  <label className='col-xl-3 col-md-4'>
                    ¿Antes de las 12pm?
                  </label>
                  <div
                    style={{
                      width: "50%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type='checkbox'
                      checked={beforeHour}
                      onChange={(e) => setBeforeHour(e.target.checked)}
                      style={{ width: "20px", height: "20px" }}
                    />
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "0.9rem",
                        color: "#555",
                      }}
                    >
                      Sí, entregar antes del horario seleccionado
                    </span>
                  </div>
                </div>
              )}
            </div>
            <button
              className='btn btn-primary'
              disabled={isSubmitting}
              type='submit'
            >
              <span>Guardar</span>
            </button>
          </Form>
        );
      }}
    </Formik>
  );
};

export default VerAddressForm;
